<template>
  <UiOverlayMessage
    :title="errorTitle"
    :action-label="actionLabel"
    data-testid="overlay-error-root"
    @action="onActionClick"
  />
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUiStore } from '@/app/stores/ui';
import { sentryService } from '@/services/telemetry/sentry';
import { track } from '@/shared/utils/track-telemetry';
// biome-ignore lint/correctness/noUnusedImports: used via tag <UiOverlayMessage> in <template>
import { UiOverlayMessage } from '@/ui-kit';

const ui = useUiStore();
const { t, te } = useI18n();

const context = computed(() => ui.getCurrentError()?.context ?? null);

const error = computed(
  () =>
    context.value?.error ?? {
      code: 'unknown' as string | undefined,
      message: 'Unknown error',
      name: 'Error',
    }
);

const errorTitle = computed(() => {
  // Guarded end-to-end: vue-i18n's message compiler throws SyntaxError on
  // strings containing its special syntax (`@:`, `{`, `|`) — and error codes
  // here can be arbitrary server text. The error overlay must never crash on
  // top of the error it is reporting (KIOSK-FRONTEND-Z1/ZZ).
  try {
    const key = `errors.${error.value.code}`;
    if (te(key)) return t(key);
    // English fallback when current-locale key is missing.
    if (te(key, 'en-GB')) return t(key, {}, { locale: 'en-GB' });
  } catch {
    // Fall through to the raw message below.
  }
  return error.value.message || error.value.code || '';
});

const actionLabel = computed(() => {
  const buttonType = context.value?.fallback?.button;
  if (!buttonType) return undefined;
  try {
    const key = `OverlayError.buttons.${buttonType}`;
    if (te(key)) return t(key);
    if (te(key, 'en-GB')) return t(key, {}, { locale: 'en-GB' });
  } catch {
    // Untranslatable button label — render the overlay without an action
    // label rather than crash it.
  }
  return undefined;
});

let isRetryInFlight = false;

async function onActionClick(): Promise<void> {
  // Touch-screen double-tap protection: without this, two concurrent
  // hardRestart() calls would race on session.end() + window.location.reload().
  if (isRetryInFlight) return;
  isRetryInFlight = true;

  sentryService.addBreadcrumb('error.recovery', 'User clicked try again', {
    error_code: error.value?.code,
  });

  try {
    await context.value?.fallback?.callback?.();
  } finally {
    isRetryInFlight = false;
  }
}

onMounted(() => {
  track('[Sentry] Breadcrumb failed:', () => {
    sentryService.addBreadcrumb('error.display', 'Error overlay displayed', {
      error_code: error.value?.code,
      error_message: error.value?.message?.slice(0, 100),
      has_fallback: Boolean(context.value?.fallback?.callback),
    });
  });
});
</script>
